import { useCallback, useEffect, useRef } from 'react';

import {
  fetchUserEvents,
  markUserEventsRead,
} from '../services/bulkUserImportService';
import { BULK_IMPORT_COMPLETED } from '../userEventKinds';

export { BULK_IMPORT_COMPLETED };

export const EVENT_POLL_INTERVAL = 5000;

/**
 * Collects finished-work notices and hands them to `onEvents` (PD-4066).
 *
 * This is what makes the design's toasts trustworthy. The import runs in a
 * worker and the wizard tells the admin they may close it, so the outcome
 * cannot be something the wizard is holding on to — it is a row on the server,
 * and this asks for it from whatever page the admin is on.
 *
 * Events are marked read only after `onEvents` has run, so a toast that was
 * never raised is collected again on the next tick instead of being lost.
 */
export default function useUserEvents(
  onEvents,
  // pollInterval is a test seam, as in useBulkUserImport: the real 5s cadence
  // outlasts Jest's per-test limit, and fake timers are not usable here because
  // React 16's async flush rides on setImmediate, which Jest also fakes.
  { enabled = true, pollInterval = EVENT_POLL_INTERVAL } = {},
) {
  const isMounted = useRef(true);
  // The callback changes identity on every render of the page that owns it;
  // keeping it in a ref means the interval below is created once rather than
  // being torn down and restarted each time.
  const handler = useRef(onEvents);
  const inFlight = useRef(false);

  useEffect(() => {
    handler.current = onEvents;
  }, [onEvents]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const collect = useCallback(async () => {
    // A poll slower than the interval would otherwise overlap the next one, and
    // both would see the same unread events: the handler would fire twice, so
    // the admin would get the same toast twice, and both would call
    // markUserEventsRead. Skipping a tick is harmless — the events stay unread
    // on the server and the next tick collects them.
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      const events = await fetchUserEvents([BULK_IMPORT_COMPLETED]);
      if (!isMounted.current || !events.length) return;

      handler.current(events);
      await markUserEventsRead(events.map((event) => event.id));
    } catch {
      // Nothing to do but ask again on the next tick. The events are still on
      // the server, unread, so nothing has been lost.
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    // Once immediately, so an import that finished while the admin was on
    // another page surfaces on arrival rather than after the first interval.
    collect();

    const timer = setInterval(collect, pollInterval);
    return () => clearInterval(timer);
  }, [collect, enabled, pollInterval]);
}
